import { useState } from "react";
import Button from "@/components/base/Button";
import WizardProgress from "./WizardProgress";
import StepTipo from "./StepTipo";
import StepDatos from "./StepDatos";
import StepVehiculos from "./StepVehiculos";
import StepServicios from "./StepServicios";
import StepDocumentos from "./StepDocumentos";
import StepRevision from "./StepRevision";
import StepEnviar from "./StepEnviar";
import SuccessPanel from "./SuccessPanel";
import { useAfiliacionForm } from "./useAfiliacionForm";
import { WIZARD_STEPS } from "./types";
import type { AffiliateType } from "@/types";

export default function TrabajaWizard() {
  const wizard = useAfiliacionForm();
  const [accepted, setAccepted] = useState(false);
  const { form, step, status } = wizard;
  const isLast = step === WIZARD_STEPS.length - 1;

  if (status === "success") {
    return (
      <SuccessPanel
        numero={wizard.numero}
        onReset={() => {
          setAccepted(false);
          wizard.reset();
        }}
      />
    );
  }

  const renderStep = () => {
    switch (WIZARD_STEPS[step].id) {
      case "tipo":
        return (
          <StepTipo
            value={form.tipo}
            error={wizard.errors.tipo}
            onChange={(value: AffiliateType) => wizard.setField("tipo", value)}
          />
        );
      case "datos":
        return <StepDatos form={form} setField={wizard.setField} errors={wizard.errors} />;
      case "vehiculos":
        return (
          <StepVehiculos
            form={form}
            addVehiculo={wizard.addVehiculo}
            removeVehiculo={wizard.removeVehiculo}
            updateVehiculo={wizard.updateVehiculo}
            errors={wizard.errors}
          />
        );
      case "servicios":
        return (
          <StepServicios
            form={form}
            toggleServicio={wizard.toggleServicio}
            setField={wizard.setField}
            errors={wizard.errors}
          />
        );
      case "documentos":
        return (
          <StepDocumentos form={form} setDocumentosGenerales={wizard.setDocumentosGenerales} />
        );
      case "revision":
        return <StepRevision form={form} goTo={wizard.goTo} />;
      default:
        return (
          <StepEnviar form={form} accepted={accepted} onAcceptedChange={setAccepted} />
        );
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
      <WizardProgress current={step} onGoTo={wizard.goTo} />

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        {renderStep()}

        {wizard.formError && (
          <p
            role="alert"
            className="mt-6 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {wizard.formError}
          </p>
        )}

        <div className="mt-7 flex flex-col gap-3 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={wizard.prev}
            disabled={step === 0}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-line text-base leading-none" aria-hidden="true"></i>
            Anterior
          </button>

          {isLast ? (
            <Button
              type="button"
              variant="primary"
              size="lg"
              icon={status === "loading" ? "ri-loader-4-line" : "ri-send-plane-line"}
              onClick={() => void wizard.handleSubmit()}
              disabled={status === "loading" || !accepted}
              className="w-full sm:w-auto"
            >
              {status === "loading" ? "Enviando…" : "Enviar solicitud"}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="lg"
              iconRight="ri-arrow-right-line"
              onClick={wizard.next}
              className="w-full sm:w-auto"
            >
              Siguiente
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}